import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface Props {
  codigo: string;
  /** Clases del contenedor según dónde va (capa, márgenes). */
  className?: string;
  /** Tamaño, color y ancho máximo del texto según dónde va. */
  textoClassName?: string;
  /** Etiqueta antes del código («Código», en el header de la obra). Con etiqueta, el botón de copiar aparece EN SU
   *  LUGAR y no al costado del código (Juan, 29/9): con mouse, al pasar por el dato la etiqueta se va y aparece el
   *  ícono, sin mover nada; en táctil no hay hover, así que el ícono queda en ese lugar. */
  etiqueta?: string;
}

// Código de obra de la Lista CC con su botón de copiar: patrón `CopiableNumber` de RISIT (MVP_HIGHLIGHTS.md, Juan
// 9/7). El click copia el código completo aunque se vea truncado. En la card de la home (sin etiqueta) el ícono va
// después del código y aparece al pasar el mouse; en táctil queda siempre visible. En el header de la obra (con
// etiqueta) ocupa el lugar de la etiqueta (Juan, 29/9: «sobre la label y al hover, no al costado»).
export function CodigoObraCopiable({ codigo, className, textoClassName, etiqueta }: Props) {
  const { isCopied: copiado, copyToClipboard } = useCopyToClipboard({
    timeout: 1200,
    onError: () => toast.error('No se pudo copiar (permiso del navegador)'),
  });

  const boton = (clases: string) => (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            // trim: en la Lista CC hay códigos con un espacio al final (la 1410, «CA70050 »); el «- -» de los
            // expedientes de GDE («EX-2021-86220015- -APN-SG#SOFSE») es el formato oficial y se copia tal cual.
            onClick={() => copyToClipboard(codigo.trim())}
            aria-label={copiado ? 'Copiado' : `Copiar código ${codigo}`}
            className={cn('flex-none text-text-tertiary transition-opacity', clases, copiado && 'text-success opacity-100')}
          />
        }
      >
        {copiado ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </TooltipTrigger>
      <TooltipContent>{copiado ? 'Copiado' : 'Copiar código'}</TooltipContent>
    </Tooltip>
  );

  const texto = <span className={cn('truncate font-mono tracking-[0.02em]', textoClassName)}>{codigo}</span>;

  if (etiqueta) {
    return (
      <span data-codigo-obra="" className={cn('group/cod inline-flex min-w-0 items-center gap-1', className)}>
        {/* La etiqueta conserva su lugar (opacity, no display): el botón la tapa con su mismo tamaño y el código no
            se mueve. Se va con hover, con foco de teclado en el botón, mientras dura el «Copiado» y siempre en táctil. */}
        <span className="relative inline-flex flex-none">
          <span
            className={cn(
              'text-text-tertiary transition-opacity group-hover/cod:opacity-0 [[data-codigo-obra]:has(:focus-visible)_&]:opacity-0 [@media(hover:none)]:opacity-0',
              copiado && 'opacity-0',
            )}
          >
            {etiqueta}
          </span>
          {boton(
            "absolute inset-0 h-full w-full opacity-0 focus-visible:opacity-100 group-hover/cod:opacity-100 [@media(hover:none)]:h-full [@media(hover:none)]:w-full [@media(hover:none)]:opacity-100 [@media(hover:none)]:after:absolute [@media(hover:none)]:after:-inset-y-[12px] [@media(hover:none)]:after:inset-x-0 [@media(hover:none)]:after:content-['']",
          )}
        </span>
        {texto}
      </span>
    );
  }

  return (
    <span data-codigo-obra="" className={cn('group/cod inline-flex min-w-0 items-center gap-1', className)}>
      {texto}
      {boton('opacity-0 focus-visible:opacity-100 group-hover/cod:opacity-100 [@media(hover:none)]:opacity-100')}
    </span>
  );
}
